//External Libraries
import { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { faFilter as farFilter } from "@fortawesome/pro-regular-svg-icons/faFilter";
import { faChevronDown as farChevronDown } from "@fortawesome/pro-regular-svg-icons/faChevronDown";
import { faChevronRight as farChevronRight } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import { useTranslation } from "react-i18next";

//Components
import { CMButton } from "@chartmetric/chartmetric-design-system/components/CMButton";
import { CMFlex } from "@chartmetric/chartmetric-design-system/components/CMFlex";
import { CMIcon } from "@chartmetric/chartmetric-design-system/components/CMIcon";

//Utils
import {
  getTranslatedCategoryName,
  ANNOUNCEMENT_PLAN_ORDER,
  PLAN_COLOR_VARS,
} from "../utils";

//Types
import type { AnnouncementCategory, AnnouncementPlan } from "../types";

interface AnnouncementControlProps {
  categories: AnnouncementCategory[];
  selectedCategories: string[];
  onCategoryFilter: (categories: string[]) => void;
  selectedPlans: string[];
  onSelectedPlansChange: (plans: string[]) => void;
}

type GroupState = "all" | "some" | "none";

function getGroupState(selected: string[], all: string[]): GroupState {
  if (all.length === 0) return "none";
  const count = all.filter((v) => selected.includes(v)).length;
  if (count === 0) return "none";
  if (count === all.length) return "all";
  return "some";
}

function AnnouncementControl({
  categories,
  selectedCategories,
  onCategoryFilter,
  selectedPlans,
  onSelectedPlansChange,
}: AnnouncementControlProps) {
  const { t, i18n } = useTranslation("common", {
    keyPrefix: "announcementPanel",
  });

  const [open, setOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(true);
  const [plansOpen, setPlansOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const categoryValues = useMemo(
    () => categories.map((c) => c.name),
    [categories],
  );

  const planValues: string[] = useMemo(
    () => [...ANNOUNCEMENT_PLAN_ORDER],
    [],
  );

  const categoryGroupState = getGroupState(selectedCategories, categoryValues);
  const planGroupState = getGroupState(selectedPlans, planValues);

  const handleCategoryParentToggle = useCallback(() => {
    if (categoryGroupState === "all") {
      onCategoryFilter([]);
    } else {
      onCategoryFilter(categoryValues);
    }
  }, [categoryGroupState, categoryValues, onCategoryFilter]);

  const handlePlanParentToggle = useCallback(() => {
    if (planGroupState === "all") {
      onSelectedPlansChange([]);
    } else {
      onSelectedPlansChange(planValues);
    }
  }, [planGroupState, planValues, onSelectedPlansChange]);

  const handleCategoryToggle = useCallback(
    (value: string) => {
      if (selectedCategories.includes(value)) {
        onCategoryFilter(selectedCategories.filter((v) => v !== value));
      } else {
        onCategoryFilter([...selectedCategories, value]);
      }
    },
    [selectedCategories, onCategoryFilter],
  );

  const handlePlanToggle = useCallback(
    (value: string) => {
      if (selectedPlans.includes(value)) {
        onSelectedPlansChange(selectedPlans.filter((v) => v !== value));
      } else {
        onSelectedPlansChange([...selectedPlans, value]);
      }
    },
    [selectedPlans, onSelectedPlansChange],
  );

  const totalSelected = selectedCategories.length + selectedPlans.length;
  const buttonText = totalSelected > 0
    ? `${t("filter")} (${totalSelected})`
    : t("filter");

  return (
    <CMFlex
      align="center"
      justify="between"
      position="sticky"
      z="z-10"
      background="bg-[--themewhite]"
      _className="top-0 flex-shrink-0"
    >
      <div ref={containerRef} style={{ position: "relative" }}>
        <CMButton
          iconLeft={farFilter}
          text={buttonText}
          variant="secondaryOutline"
          size="sm"
          onClick={() => setOpen((prev) => !prev)}
        />
        {open && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              right: 0,
              zIndex: 1001,
              minWidth: 210,
              background: "var(--themewhite, #fff)",
              border: "1px solid var(--cm-gray-200, #e5e7eb)",
              borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
              padding: "4px 0",
            }}
          >
            {categories.length > 0 && (
              <div>
                <CollapsibleGroupHeader
                  label={t("categoriesGroup")}
                  isOpen={categoriesOpen}
                  groupState={categoryGroupState}
                  onToggleOpen={() => setCategoriesOpen((v) => !v)}
                  onToggleAll={handleCategoryParentToggle}
                />
                {categoriesOpen && categories.map((cat) => (
                  <ChildRow
                    key={cat.name}
                    label={getTranslatedCategoryName(cat, i18n.language)}
                    checked={selectedCategories.includes(cat.name)}
                    onToggle={() => handleCategoryToggle(cat.name)}
                    dotColor={cat.color}
                  />
                ))}
              </div>
            )}
            <div>
              <CollapsibleGroupHeader
                label={t("plansGroup")}
                isOpen={plansOpen}
                groupState={planGroupState}
                onToggleOpen={() => setPlansOpen((v) => !v)}
                onToggleAll={handlePlanParentToggle}
              />
              {plansOpen && ANNOUNCEMENT_PLAN_ORDER.map((plan) => (
                <ChildRow
                  key={plan}
                  label={t(`plans.${plan}`)}
                  checked={selectedPlans.includes(plan)}
                  onToggle={() => handlePlanToggle(plan)}
                  dotColorVar={PLAN_COLOR_VARS[plan as AnnouncementPlan]}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </CMFlex>
  );
}

interface CollapsibleGroupHeaderProps {
  label: string;
  isOpen: boolean;
  groupState: GroupState;
  onToggleOpen: () => void;
  onToggleAll: () => void;
}

function CollapsibleGroupHeader({
  label,
  isOpen,
  groupState,
  onToggleOpen,
  onToggleAll,
}: CollapsibleGroupHeaderProps) {
  const checked = groupState === "all";
  const indeterminate = groupState === "some";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <button
        type="button"
        onClick={onToggleOpen}
        aria-expanded={isOpen}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          color: "inherit",
          flexShrink: 0,
        }}
      >
        <CMIcon
          icon={isOpen ? farChevronDown : farChevronRight}
          size="xs"
        />
      </button>
      <IndeterminateCheckbox
        checked={checked}
        indeterminate={indeterminate}
        onChange={onToggleAll}
      />
      <button
        type="button"
        onClick={onToggleOpen}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontWeight: 600,
          fontSize: 12,
          color: "inherit",
          textAlign: "left",
        }}
      >
        {label}
      </button>
    </div>
  );
}

interface ChildRowProps {
  label: string;
  checked: boolean;
  onToggle: () => void;
  dotColor?: string;
  dotColorVar?: string;
}

function ChildRow({ label, checked, onToggle, dotColor, dotColorVar }: ChildRowProps) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 10px 5px 36px",
        cursor: "pointer",
        fontSize: 12,
        userSelect: "none",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        style={{ cursor: "pointer", flexShrink: 0 }}
      />
      {(dotColor || dotColorVar) && (
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: dotColorVar ?? dotColor,
          }}
        />
      )}
      {label}
    </label>
  );
}

interface IndeterminateCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}

function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
}: IndeterminateCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      style={{ cursor: "pointer", flexShrink: 0 }}
    />
  );
}

export default AnnouncementControl;
